import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  DEFAULT_NOTES_FILTERS,
  NotesFilters,
  filtersToSearchParams,
  parseNotesFilters,
} from '../utils/notesFilterUtils';

export function useNotesFiltersFromUrl() {
  const [searchParams, setSearchParams] = useSearchParams();

  const filters = useMemo(() => parseNotesFilters(searchParams), [searchParams]);

  const applyFilters = useCallback(
    (patch: Partial<NotesFilters>, resetPage = true) => {
      const next = {
        ...filters,
        ...patch,
        page: resetPage ? 1 : patch.page ?? filters.page,
      };
      const params = filtersToSearchParams(next);
      setSearchParams(params, { replace: true });
    },
    [filters, setSearchParams]
  );

  const setFilter = useCallback(
    <K extends keyof NotesFilters>(key: K, value: NotesFilters[K]) => {
      applyFilters({ [key]: value } as Partial<NotesFilters>);
    },
    [applyFilters]
  );

  const clearFilters = useCallback(() => {
    setSearchParams(new URLSearchParams(), { replace: true });
  }, [setSearchParams]);

  const setPage = useCallback(
    (page: number) => {
      applyFilters({ page }, false);
    },
    [applyFilters]
  );

  return {
    filters,
    setFilter,
    applyFilters,
    clearFilters,
    setPage,
    defaults: DEFAULT_NOTES_FILTERS,
  };
}
